import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import { useNavigationStore } from './navigation'
import type {
  CommandsCreateSectionCommand,
  CommandsCreateServiceDeliverablePageCommand,
  CommandsReorderCommand,
  CommandsSavePageOrderAndActiveStatusCommand,
  CommandsServiceDeliverableForRearrange,
  CommandsUpdateServiceDeliverablePageCommand,
  CommandsUploadFilesCommand,
  QueriesFileHistoryRecord,
  QueriesGetManageDeliverablesPageListResponse,
  QueriesGetServiceDeliverablesPageDetailResponse,
  TransferObjectsUserWithAccess,
} from 'client'
import type { Router } from 'vue-router'
import { MESSAGE } from '~/constants/strings'
import { HNRV2, fileNetworkRequest } from '~/helpers'
import { notifyFailure, notifySuccess } from '~/utils/notifications'
import {
  openDownloadFileDialogWithData,
  openNewTabWithData,
} from '~/helpers/file'
import { api } from '~/services'

interface ServiceDeliverablesState {
  serviceDeliverablePages: QueriesGetManageDeliverablesPageListResponse[]
  serviceDeliverableSections: any[]
  fileHistory: { records: QueriesFileHistoryRecord[]; rowCount: number }
  pageDetail: QueriesGetServiceDeliverablesPageDetailResponse | null
}

export const useServiceDeliverablesStore = defineStore('service-deliverables', {
  state: (): ServiceDeliverablesState => ({
    serviceDeliverablePages: [],

    // Elevated Page Sections
    serviceDeliverableSections: [],
    fileHistory: { records: [], rowCount: 0 },

    // Unelevated Page Sections
    pageDetail: null,
  }),
  getters: {
    serviceID(): string {
      const NavigationStore = useNavigationStore()
      return NavigationStore.activeService?.serviceID ?? ''
    },
    pageID(): string {
      const route = this.$router.currentRoute.value
      return !route.params?.serviceDeliverableID ||
        Array.isArray(route.params.serviceDeliverableID)
        ? ''
        : route.params.serviceDeliverableID
    },
  },
  actions: {
    async getServiceDeliverables(): Promise<boolean> {
      if (this.serviceID) {
        const response = await HNRV2({
          fn: () =>
            api.serviceDeliverables.apiServiceDeliverablesServiceIDPagesGet(
              this.serviceID
            ),
        })

        if (response instanceof AxiosError) {
          notifyFailure(MESSAGE.SERVICE_DELIVERABLES_GET_PAGES_FAILURE)
          return false
        }

        this.serviceDeliverablePages = response
        return true
      }

      return false
    },
    // ELEVATED SERVICE DELIVERABLE PAGE SECTIONS
    async getManageDeliverablePageDetail({
      pageID,
    }: {
      pageID: string
    }): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesPagePageIDManageGet(
            pageID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_GET_SECTIONS_FILES_FAILURE)
        return false
      }

      this.serviceDeliverableSections = response
      return true
    },
    // UNELEVATED SERVICE DELIVERABLE PAGE SECTIONS
    async getServiceDeliverablesPageDetail({
      pageID,
    }: {
      pageID: string
    }): Promise<boolean> {
      // Sending a weird page id, getting back a validation error
      // how to validate that this is a hash format?
      const response =
        await api.serviceDeliverables.apiServiceDeliverablesPagePageIDGet(
          pageID
        )

      // ! rare situation where status is used for something specific
      // ! & doesn't merge with other error states (ie don't show notifyFailure here)
      // Invalid Guid Format
      if (response.status === 400) {
        this.pageDetail = null
        return true
      }

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_GET_SECTIONS_FILES_FAILURE)
        return false
      }

      this.pageDetail = response.data
      return true
    },
    async getFileHistory({
      fileID,
      page,
      rowsPerPage,
    }: {
      fileID: string
      page: number
      rowsPerPage: number
    }): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesFileFileIDHistoryGet(
            fileID,
            page,
            rowsPerPage
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_GET_SECTIONS_FILES_FAILURE)
        return false
      }

      this.fileHistory = {
        records: response.fileHistoryRecords,
        rowCount: response.rowCount,
      }

      return true
    },
    async addServiceDeliverablePage({
      pageName,
    }: {
      pageName: string
    }): Promise<boolean> {
      const payload: CommandsCreateServiceDeliverablePageCommand = {
        serviceID: this.serviceID,
        pageName,
      }

      const pageNameAlreadyExists: boolean = this.serviceDeliverablePages.some(
        (page) => {
          return page.name.toLowerCase() === pageName.toLowerCase()
        }
      )

      if (pageNameAlreadyExists) {
        notifyFailure('A page with that name already exists')
        return false
      }

      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesPagePost(payload),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_CREATE_PAGE_FAILURE)
        return false
      }

      await this.getServiceDeliverables()
      await this.updateLeftNavigation()

      notifySuccess(`Successfully created ${pageName} page`)

      const newDeliverablePageId = this.serviceDeliverablePages.find((page) => {
        return page.name === pageName
      })?.pageID

      const NavigationStore = useNavigationStore()
      if (
        newDeliverablePageId &&
        NavigationStore.accessibleServiceDeliverableIds.includes(
          newDeliverablePageId
        ) &&
        NavigationStore.activeServiceNameForPaths
      ) {
        const newServiceDeliverablePath = `/${NavigationStore.activeServiceNameForPaths}/service-deliverables/${newDeliverablePageId}/manage`
        await (this.$router as Router).push(newServiceDeliverablePath)
        return true
      }
      notifyFailure('Problem moving to your new page, refresh and try again')
      return true
    },
    async addServiceDeliverableSection({
      sectionName,
      pageID,
    }: {
      sectionName: string
      pageID: string
    }): Promise<boolean> {
      const payload: CommandsCreateSectionCommand = {
        serviceID: this.serviceID,
        sectionName,
        pageID,
      }
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesSectionPost(payload),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_CREATE_SECTION_FAILURE)
        return false
      }

      await this.getManageDeliverablePageDetail({
        pageID,
      })
      return true
    },
    async initiateFileUpload({
      sectionID,
      payload,
    }: {
      sectionID: string
      payload: CommandsUploadFilesCommand
    }): Promise<string | undefined> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesSectionSectionIDFilesPost(
            sectionID,
            payload
          ),
      })

      if (response instanceof AxiosError) {
        return
      }

      return response
    },
    async uploadChunks({
      sectionID,
      batchID,
      currentChunkNumber,
      fileSize,
      isEndOfFile,
      chunkData,
    }: {
      sectionID: string
      batchID: string
      currentChunkNumber: number
      fileSize: number
      isEndOfFile: boolean
      chunkData: { data: any; fileName: string }
    }): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesSectionSectionIDChunkPost(
            sectionID,
            batchID,
            currentChunkNumber,
            fileSize,
            isEndOfFile,
            new File([chunkData.data], chunkData.fileName)
          ),
      })

      if (response instanceof AxiosError) {
        throw response
      }

      return false
    },
    async editServiceDeliverablePageName({
      pageID,
      pageName,
    }: CommandsUpdateServiceDeliverablePageCommand): Promise<boolean> {
      const payload: CommandsUpdateServiceDeliverablePageCommand = {
        pageID,
        pageName,
      }
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesPagePut(payload),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_EDIT_PAGE_FAILURE)
        return false
      }

      notifySuccess(MESSAGE.SERVICE_DELIVERABLES_EDIT_PAGE_SUCCESS)
      await this.getServiceDeliverables()
      await this.updateLeftNavigation()
      return true
    },
    async editServiceDeliverableSectionName({
      sectionID,
      sectionName,
      pageID,
    }: {
      sectionID: string
      sectionName: string
      pageID: string
    }): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesSectionSectionIDNameNamePut(
            sectionID,
            sectionName
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_EDIT_SECTION_FAILURE)
        return false
      }

      notifySuccess(MESSAGE.SERVICE_DELIVERABLES_EDIT_SECTION_SUCCESS)
      await this.getManageDeliverablePageDetail({
        pageID,
      })
      return true
    },
    async editServiceDeliverableFileName({
      fileID,
      fileName,
      pageID,
    }: {
      fileID: string
      fileName: string
      pageID: string
    }): Promise<boolean> {
      const payload = {
        fileID,
        fileName,
      }
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesFileFileIDNameNamePut(
            fileID,
            fileName
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_EDIT_FILE_FAILURE)
        return false
      }

      notifySuccess(MESSAGE.SERVICE_DELIVERABLES_EDIT_FILE_SUCCESS)
      await this.getManageDeliverablePageDetail({
        pageID,
      })
      return true
    },
    async deleteServiceDeliverablePage(pageID: string): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesPagePageIDDelete(
            pageID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_DELETE_PAGE_FAILURE)
        return false
      }

      await this.getServiceDeliverables()
      await this.updateLeftNavigation()
      return true
    },
    async deleteServiceDeliverableSection({
      sectionID,
      pageID,
    }: {
      sectionID: string
      pageID: string
    }): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesSectionSectionIDDelete(
            sectionID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_DELETE_SECTION_FAILURE)
        return false
      }

      await this.getManageDeliverablePageDetail({
        pageID,
      })
      return true
    },
    async deleteServiceDeliverableFile({
      fileID,
      pageID,
    }: {
      fileID: string
      pageID: string
    }): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesFileFileIDDelete(
            fileID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_DELETE_FILE_FAILURE)
        return false
      }

      await this.getManageDeliverablePageDetail({
        pageID,
      })
      return true
    },
    async savePageOrderAndActiveStatus({
      deliverablePages,
    }: {
      deliverablePages: CommandsServiceDeliverableForRearrange[]
    }): Promise<boolean> {
      const payload: CommandsSavePageOrderAndActiveStatusCommand = {
        serviceID: this.serviceID,
        deliverablePages,
      }

      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesPagesOrderAndActivePut(
            payload
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(MESSAGE.SERVICE_DELIVERABLES_SAVE_PAGE_CHANGES_FAILURE)
        return false
      }

      await this.getServiceDeliverables()
      await this.updateLeftNavigation()
      return true
    },
    async reorderSection({
      pageID,
      request,
    }: {
      pageID: string
      request?: CommandsReorderCommand | undefined
    }): Promise<boolean> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesPagePageIDOrderSectionsAndFilesPut(
            pageID,
            request
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure(
          MESSAGE.SERVICE_DELIVERABLES_SAVE_SECTION_FILES_CHANGES_FAILURE
        )
        return false
      }

      await this.getServiceDeliverables()
      return true
    },
    async downloadServiceDeliverableFile({
      fileId,
    }: {
      fileId: string
    }): Promise<boolean> {
      const response = await fileNetworkRequest({
        fn: () =>
          api.serviceDeliverablesFile.apiServiceDeliverablesFileFileIDGet(
            fileId
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to download file')
        return false
      }

      openDownloadFileDialogWithData({
        response,
      })
      return true
    },
    async updateSectionPermissions({
      sectionID,
      selectedUsers,
    }: {
      sectionID: string
      selectedUsers: string[]
    }): Promise<boolean | string> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesSectionSectionIDUpdateAccessPost(
            sectionID,
            selectedUsers
          ),
      })

      if (response instanceof AxiosError) {
        return 'Unable to update permissions'
      }

      return true
    },
    async getSectionUsersPermissions(
      sectionID: string
    ): Promise<TransferObjectsUserWithAccess[] | null> {
      const response = await HNRV2({
        fn: () =>
          api.serviceDeliverables.apiServiceDeliverablesSectionSectionIDUsersGet(
            sectionID
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to get section user permissions')
        return null
      }

      return response
    },
    async getSectionUsers(
      sectionID: string
    ): Promise<TransferObjectsUserWithAccess[]> {
      const response = await HNRV2({
        fn: () => api.users.apiIdentityUsersServiceDeliverablesGet(sectionID),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to get section users')
        return []
      }

      return response as TransferObjectsUserWithAccess[]
    },
    async previewServiceDeliverableFile({
      fileId,
    }: {
      fileId: string
    }): Promise<boolean> {
      const response = await fileNetworkRequest({
        fn: () =>
          api.serviceDeliverablesFile.apiServiceDeliverablesFileFileIDPreviewGet(
            fileId
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to preview file')
        return false
      }

      openNewTabWithData({ data: response.data })
      return true
    },
    async getFilesForSection({
      sectionId,
    }: {
      sectionId: string
    }): Promise<boolean> {
      const response = await fileNetworkRequest({
        fn: () =>
          api.serviceDeliverablesFile.apiServiceDeliverablesSectionSectionIDFilesGet(
            sectionId
          ),
      })

      if (response instanceof AxiosError) {
        notifyFailure('Unable to download files from section')
        return false
      }

      openDownloadFileDialogWithData({
        response,
      })
      return true
    },
    async updateLeftNavigation() {
      const NavigationStore = useNavigationStore()
      await NavigationStore.setNavigationItems({
        serviceID: this.serviceID,
        forceUpdate: true,
      })
    },
  },
})
